import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API } from '@constants/api';
import { API_BASE_URL } from '@constants/injection_tokens';
import { FilterOperation } from '@shared/models/filterOperations';
import { toRequestDetailsDTO } from 'src/app/features/characteristic/models/requestDetailsDTO';
import { MedikoRequestType } from 'src/app/features/mediko-requests/models/meidko-request-type.model';
import { RequestType } from '../models/requests';
import { CreateMailCampaign, CreateMailCampaignCompanyDTO } from 'src/app/features/mail-campaign/models/mail-campaign';
import { Observable } from 'rxjs';
import { GenericResponse } from '@shared/models/http';

@Injectable({ providedIn: 'root' })
export class RequestsService {
  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private baseUrl: string
  ) {}

  getWorkerList(startDate: any, endDate: any, statusId: any) {
    const url = `${this.baseUrl}/mediko/request/requests`;
    let params: any = {
      'RequestType.Operator': FilterOperation.Equal,
      'RequestType.Values': [RequestType.WorkerList],
    };
    if (statusId) {
      params = {
        ...params,
        'StatusId.Values': statusId ? [statusId] : [],
        'StatusId.Operator': FilterOperation.Equal,
      };
    }

    if (startDate) {
      params = {
        ...params,
        'StartDate.Operator': FilterOperation.GreaterThanOrEqual,
        'StartDate.Values': [startDate],
      };
    }

    if (endDate) {
      params = {
        ...params,
        'EndDate.Operator': FilterOperation.LessThanOrEqual,
        'EndDate.Values': [endDate],
      };
    }
    return this.http.get(url, {
      params: new HttpParams({ fromObject: params }),
    });
  }

  getSickLeaveList(startDate: any, endDate: any, statusId: any) {
    const url = `${this.baseUrl}/mediko/request/requests`;
    let params: any = {
      'RequestType.Operator': FilterOperation.Equal,
      'RequestType.Values': [RequestType.SickLeave],
    };
    if (statusId) {
      params = {
        ...params,
        'StatusId.Values': statusId ? [statusId] : [],
        'StatusId.Operator': FilterOperation.Equal,
      };
    }

    if (startDate) {
      params = {
        ...params,
        'StartDate.Operator': FilterOperation.GreaterThanOrEqual,
        'StartDate.Values': [startDate],
      };
    }

    if (endDate) {
      params = {
        ...params,
        'EndDate.Operator': FilterOperation.LessThanOrEqual,
        'EndDate.Values': [endDate],
      };
    }
    return this.http.get(url, {
      params: new HttpParams({ fromObject: params }),
    });
  }

  getEmailList(pageNumber: number, pageSize: number, userId?: number) {
    const url = `${this.baseUrl}/mediko/mails/inbox`;
    let params: any = {
      'PageNumber': pageNumber,
      'PageSize': pageSize,
      'UserId.Values': userId,
      'UserId.Operator': 'Equal',
      'OrderBy':"Id desc"
    };
   
    return this.http.get(url, {
      params: new HttpParams({ fromObject: params }),
    });
  }
  getSentEmailList(pageNumber: number, pageSize: number, userId?: number) {
    const url = `${this.baseUrl}/mediko/mails/sent`;
    let params: any = {
      'PageNumber': pageNumber,
      'PageSize': pageSize,
      'UserId.Values': userId,
      'UserId.Operator': 'Equal',
      'OrderBy': "Id desc"
    };

    return this.http.get(url, {
      params: new HttpParams({ fromObject: params }),
    });
  }


  getEmail(mailId: number) {
    const url = `${this.baseUrl}/mediko/mails/inbox/${mailId}`;
  
    return this.http.get(url);
  }

  syncMails() {
    const url = `${this.baseUrl}/mediko/mails/sync-mails`;
    return this.http.get(url, {});
  }

  createRequest(type: MedikoRequestType, data?: any) {
    const url = `${this.baseUrl}/mediko/request`;
    return this.http.post(url, { requestTypeId: type, ...data });
  }
  sendMail(data: any, userId?: number) {
    const url = `${this.baseUrl}/mediko/mails/send/${userId}`;
    return this.http.post(url, data);
  }
  savePersonalSettings(data: any, userId?: number) {
    const url = `${this.baseUrl}/mediko/mails/savePersonalSettings/${userId}`;
    return this.http.post(url, data);
  }
  copyRequest(requestId: any) {
    const url = `${this.baseUrl}${API.REQUESTS.CopyRequest}/${requestId}`;
    return this.http.post(url, null);
  }
  addCharacteristicsRequestDetails(data: any) {
    const requestDto = toRequestDetailsDTO(data);

    return this.http.post(
      `${this.baseUrl}/mediko/production-characteristic/add-details`,
      requestDto
    );
  }

  export(data: any) {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');

    return this.http.post(
      `${this.baseUrl}/mediko/production-characteristic/generate-document`,
      toRequestDetailsDTO(data),
      {
        headers,
        responseType: 'blob',
      }
    );
  }

  exportPreview(id: number) {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');

    return this.http.post(
      `${this.baseUrl}/mediko/production-characteristic/generate-document-preview`,
      id,
      {
        headers,
        responseType: 'blob',
      }
    );
  }

  createMedikoRequest(type: MedikoRequestType, data?: any) {
    const url = `${this.baseUrl}/mediko/request/staff`;
    return this.http.post(url, { requestTypeId: type, ...data });
  }

  updateRequestStatus(data?: any) {
    const url = `${this.baseUrl}/mediko/request/status-update`;
    return this.http.post(url, data);
  }
  copyCharacterristic(data?: any) {
    const url = `${this.baseUrl}/mediko/production-characteristic/copy-characterristic`;
    return this.http.post(url, data);
  }

  updateRequestDocumentStatus(data?: any) {
    const url = `${this.baseUrl}/mediko/request/document/status-update`;
    return this.http.post(url, data);
  }

  createMailCampaign(data: CreateMailCampaign): Observable<GenericResponse<CreateMailCampaign>> {
    const url = `${this.baseUrl}${API.MAIL_CAMPAIGN.CREATE}`;
    return this.http.post<GenericResponse<CreateMailCampaign>>(url, data);
  }

  updateMailCampaign(mailCampaignId: number, data: CreateMailCampaign): Observable<GenericResponse<CreateMailCampaignCompanyDTO[]>> {
    const url = `${this.baseUrl}${API.MAIL_CAMPAIGN.UPDATE}${mailCampaignId}`;
    return this.http.post<GenericResponse<CreateMailCampaignCompanyDTO[]>>(url, data);
  }
}
